import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import { ListCell } from './cells'
import { type ListColumnHeaders, ListColumnValueType, ListHeader } from './header'
import { ListBody } from './body'
import { Pagination } from '@src/elements/pagination/pagination'
import { convertLocalDateToDate } from '@src/util/date-format'
import { DEFAULT_PAGE_SIZE } from '@src/util/constants'
import { Loader } from '@src/components/loading-screen/loader/loader'

import './table-list.sass'
import { MemberFilters } from '@src/state/store'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { Link } from '@src/elements/link/link'

@Component({
  computed: {
    filters () {
      return this.$store.state.memberFilters
    }
  }
})

export class TableList extends Vue {

  @Prop({ required: true })
  items: Array<any>

  @Prop({ required: true })
  cells: { [key: string]: ListCell }

  @Prop({ required: false })
  header: ListColumnHeaders

  @Prop({ required: true, default: 1 })
  currentPage: number

  @Prop({ required: false })
  changePageCallback: (currentPage: number) => void

  @Prop({ required: false })
  callbackOnNewItemsOnPage: (items: Array<any>) => void

  @Prop({ required: false })
  totalItems: number

  @Prop({ required: false, default: false })
  itemsArePaginated: boolean

  @Prop({ required: false, default: false })
  loading: boolean

  protected pageSize = DEFAULT_PAGE_SIZE
  private sortKey: string | null = null
  private sortDir: 'desc' | 'asc' = 'asc'
  private sortType: ListColumnValueType = 'string'

  private filters: MemberFilters

  sortBy (sortKey, sortDir, type) {
    this.sortKey = sortKey
    this.sortDir = sortDir
    this.sortType = type
    this.changePage(1)
  }

  mounted () {
    this.$on('sortBy', this.sortBy)
  }

  destroyed () {
    this.$off('sortBy')
  }

  resetFilters () {
    return this.$store.dispatch('filterMembers', { name: '', state: undefined, toBeDeactivated: false, flags: new Set([]), page: 1 })
  }

  render (h: CreateElement) {
    return <div class='table-list'>

      {this.header ? <ListHeader columns={this.header} /> : null}

      {this.loading ? <Loader/> : this.items.length > 0 ?
        <ListBody items={this.paginate(this.order(this.items))} cells={this.cells} />
      : <ErrorScreen
          message={this.filters.name ? this.$t('PAGE.MEMBERS.LIST.NOT_FOUND.FILTER.NAME', { query: this.filters.name }) : this.$t('PAGE.MEMBERS.LIST.NOT_FOUND.FILTER.GENERIC')}
          icon='brand/members'>
          <Link variant='colored' ref='resetFilters' align='center' click={this.resetFilters} label={this.$t('PAGE.MEMBERS.LIST.RESET_FILTERS')} />
        </ErrorScreen>
      }

      <Pagination resizePage={this.resizePage}
                  changePage={this.changePage}
                  currentPage={this.currentPage}
                  pageSize={this.pageSize}
                  total={this.totalItems || this.items.length} />
    </div>
  }

  protected changePage (page) {
    if (this.changePageCallback) {
      this.changePageCallback(page)
    }
  }

  protected resizePage (size) {
    this.pageSize = size
  }

  protected paginate (items) {
    if (this.itemsArePaginated) {
      if (this.callbackOnNewItemsOnPage) this.callbackOnNewItemsOnPage(this.items)
      return this.items
    }

    let start = (this.currentPage - 1) * this.pageSize
    const newItems = items.slice(start, start + this.pageSize)
    if (this.callbackOnNewItemsOnPage) this.callbackOnNewItemsOnPage(newItems)
    // when last page is empty go to prev page
    if (!newItems.length && this.currentPage > 1) this.changePage(this.currentPage - 1)
    return newItems
  }

  protected order (items) {
    if (!this.sortKey) return items
    return items.slice().sort(this.orderValuesByType)
  }

  private orderValuesByType (a, b) {
    let order: number = 0
    switch (this.sortType) {
      case 'float':
        let floatA = parseFloat(a[this.sortKey!])
        let floatB = parseFloat(b[this.sortKey!])
        order = floatA > floatB ? 1 : floatA < floatB ? -1 : 0
        break
      case 'date':
        let dateA = convertLocalDateToDate(a[this.sortKey!])
        let dateB = convertLocalDateToDate(b[this.sortKey!])
        order = dateA > dateB ? 1 : dateA < dateB ? -1 : 0
        break
      case 'string':
      default:
        let stringA = a[this.sortKey!] ? a[this.sortKey!].toLowerCase() : 'null'
        let stringB = b[this.sortKey!] ? b[this.sortKey!].toLowerCase() : 'null'
        order = stringA > stringB ? 1 : stringA < stringB ? -1 : 0
        break
    }
    return this.sortDir === 'desc' ? -1 * order : order
  }
}
